import type { ReactNode } from 'react';
import type { FieldError } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import type { DateValidationError } from '@mui/x-date-pickers';

import type { DateFieldConfig } from '../../config';
import type { SectionKey } from '../../lease-details.types';
import { LeaseDatePicker } from '../../inputs/lease-date-input';
import { useLeaseDetailsDateLinking } from '../../lease-details-date-linking.context';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';
import { useFieldEventContextBuilder } from '../use-field-event-context';
import { createDataTestId } from '@/utils/createDataTestId';
import { DateFormat } from '@/shared/libs/time.lib';

export type DateFieldProps = {
  path: string;
  field: DateFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
  label: ReactNode;
  helperText: ReactNode;
};

export const DateField = ({
  path,
  field,
  sectionKey,
  arrayIndex,
  required,
  disabled,
  label,
  helperText,
}: DateFieldProps) => {
  const { watch, setValue, trigger, getFieldState, formState } = useFormContext();
  const { inputSize: size, asteriskColor } = useLeaseDetailsUI();
  const linkedDates = useLeaseDetailsDateLinking();

  const raw = watch(path) as string | null | undefined;
  const parsed = raw ? dayjs(raw) : null;
  const value = parsed?.isValid() ? parsed : null;
  const error = getFieldState(path, formState).error as FieldError | undefined;

  const buildContext = useFieldEventContextBuilder<string | null>(path, sectionKey, arrayIndex);
  const dateLinkingConfig = linkedDates?.config;
  const isLinkedStartDate = path === dateLinkingConfig?.startField;
  const isLinkedEndDate = path === dateLinkingConfig?.endField;
  const isLinkedDateField = isLinkedStartDate || isLinkedEndDate;

  const formatLinkedDateValue = (next: dayjs.Dayjs | null): string | null => {
    const valueFormat = dateLinkingConfig?.valueFormat ?? 'iso';
    if (!next) {
      return valueFormat === 'calendar' ? '' : null;
    }
    if (valueFormat === 'calendar') {
      return next.format(DateFormat.Calendar);
    }
    return next.toISOString();
  };

  const triggerLinkedFields = () => {
    if (!dateLinkingConfig?.startField || !dateLinkingConfig?.endField) return;
    trigger(dateLinkingConfig.endField);
    trigger(dateLinkingConfig.startField);
  };

  const handleChange = (next: dayjs.Dayjs | null) => {
    if (next !== null && !next.isValid()) return;

    if (isLinkedDateField && dateLinkingConfig?.startField && dateLinkingConfig?.endField) {
      const stored = formatLinkedDateValue(next);
      const trackedValue = stored ?? '';
      if (isLinkedStartDate) {
        dateLinkingConfig.onStartDateChanged?.(trackedValue);
        setValue(dateLinkingConfig.startField, stored, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        field.events?.onChange?.(buildContext(stored));

        if (next && !linkedDates?.endDateManuallyEdited.current) {
          const leaseDurationMonths = dateLinkingConfig.leaseDurationMonths ?? 12;
          const derivedEndDate = next.add(leaseDurationMonths, 'month').subtract(1, 'day');
          const derivedStored = formatLinkedDateValue(derivedEndDate);
          setValue(dateLinkingConfig.endField, derivedStored, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      } else if (isLinkedEndDate) {
        dateLinkingConfig.onEndDateChanged?.(trackedValue);
        if (linkedDates) {
          linkedDates.endDateManuallyEdited.current = next !== null;
        }
        setValue(dateLinkingConfig.endField, stored, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        field.events?.onChange?.(buildContext(stored));
      }

      triggerLinkedFields();
      return;
    }

    const stored = next ? next.toISOString() : null;
    setValue(path, stored, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    trigger(path);
    field.events?.onChange?.(buildContext(stored));
  };

  // Validation state is owned by the resolver — no value capture here.
  // `handleChange` already writes valid values with the right RHF flags;
  // writing on error without flags would desync isDirty/isTouched/isValid.
  const handleError = (_err: DateValidationError) => {};

  const minDate = field.minDateOffsetDays
    ? dayjs().subtract(field.minDateOffsetDays, 'day')
    : undefined;
  const maxDate = field.maxDateOffsetDays ? dayjs().add(field.maxDateOffsetDays, 'day') : undefined;

  const dataTestId = createDataTestId({ inputString: path, suffix: 'input' });

  return (
    <LeaseDatePicker
      title={typeof label === 'string' ? label : ''}
      value={value}
      onChange={handleChange}
      onError={handleError}
      error={error}
      required={required}
      disabled={disabled}
      dataTestId={dataTestId}
      minDate={minDate}
      maxDate={maxDate}
      size={size}
      redAsterisk={asteriskColor == null}
      asteriskColor={asteriskColor}
      helperText={helperText}
    />
  );
};
